import { useReducer, useCallback, useMemo, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { addToCart } from '@homeplay/app';
import { LOTTO_CONSTANTS } from '../../../constants';
import { useStakeInput } from '../../../hooks/useStakeInput';
import { useBallCounts } from '../../../hooks/useBallCounts';
import { useAssistantActions } from '../../../hooks/useLottoAssistantContext';
import { calculatePotentialPayout } from '../../../utils/common';
import { buildCartItemFromTicket } from '../../../utils/cartItemBuilder';
import { validateStake, validateBalls } from '../utils/validationPipeline';
import { createInitialState, ticketReducer } from '../utils/ticketReducer';

const TICKET_UPDATE_DEBOUNCE_MS = 600;

export const useTicketEditor = ({ ticket, onTicketUpdate, trackLottoEvent }) => {
  const reduxDispatch = useDispatch();
  const { setFlyingTicketSource } = useAssistantActions();

  const minStake = LOTTO_CONSTANTS.MIN_STAKE;
  const maxStake = LOTTO_CONSTANTS.MAX_STAKE;
  const initialStake = Math.max(
    minStake,
    Math.min(ticket.stake, LOTTO_CONSTANTS.MAX_STAKE)
  );

  const [state, dispatch] = useReducer(
    ticketReducer,
    createInitialState(ticket, initialStake)
  );

  const trackLottoEventRef = useRef(trackLottoEvent);
  const emitTimersRef = useRef({});
  const pendingChangesRef = useRef({});
  const onTicketUpdateRef = useRef(onTicketUpdate);
  const baseTicketRef = useRef(ticket);
  const stateRef = useRef(state);
  const preEditStakeRef = useRef(initialStake);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  useEffect(() => {
    trackLottoEventRef.current = trackLottoEvent;
  }, [trackLottoEvent]);

  useEffect(() => {
    onTicketUpdateRef.current = onTicketUpdate;
  }, [onTicketUpdate]);

  useEffect(() => {
    baseTicketRef.current = ticket;
  }, [
    ticket.ticket_id,
    ticket.draw_id,
    ticket.lottery_name,
    ticket.market_type,
    ticket.outcome?.id,
  ]);

  useEffect(() => {
    const timers = emitTimersRef.current;
    return () => {
      Object.keys(timers).forEach((field) => {
        const timer = timers[field];
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  const emitFieldChange = useCallback((field, oldValue, newValue) => {
    const existingTimer = emitTimersRef.current[field];
    const existingPending = pendingChangesRef.current[field];

    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    pendingChangesRef.current[field] = {
      from: existingPending ? existingPending.from : oldValue,
      to: newValue,
      occurredAt: new Date().toISOString(),
    };

    emitTimersRef.current[field] = setTimeout(() => {
      const pending = pendingChangesRef.current[field];
      delete emitTimersRef.current[field];
      delete pendingChangesRef.current[field];
      const ticketId = baseTicketRef.current.ticket_id;
      if (!ticketId || !pending) return;
      const { occurredAt, ...change } = pending;
      const normalizedChange =
        field === 'stake'
          ? {
              from: change.from * LOTTO_CONSTANTS.CENTS_MULTIPLIER,
              to: change.to * LOTTO_CONSTANTS.CENTS_MULTIPLIER,
            }
          : change;
      trackLottoEventRef.current?.(
        'ticket_updated',
        {
          ticket_id: ticketId,
          changes: { [field]: normalizedChange },
        },
        occurredAt
      );
    }, TICKET_UPDATE_DEBOUNCE_MS);
  }, []);

  const stakeInputHook = useStakeInput({
    initialStake,
    minStake,
    maxStake,
    onBlur: (stake) => emitFieldChange('stake', preEditStakeRef.current, stake),
  });

  useEffect(() => {
    if (stakeInputHook.isEditing) {
      preEditStakeRef.current = stakeInputHook.stake;
    }
  }, [stakeInputHook.isEditing]);

  useEffect(() => {
    dispatch({ type: 'SET_STAKE', payload: stakeInputHook.stake });
  }, [stakeInputHook.stake]);

  useEffect(() => {
    dispatch({ type: 'SET_STAKE_INPUT', payload: stakeInputHook.stakeInput });
  }, [stakeInputHook.stakeInput]);

  useEffect(() => {
    dispatch({ type: 'SET_IS_EDITING_STAKE', payload: stakeInputHook.isEditing });
  }, [stakeInputHook.isEditing]);

  const hasBalls = ticket.balls != null && ticket.balls.length > 0;
  const ballCountsData = useBallCounts({
    hasBalls,
    marketType: ticket.market_type,
    balls: ticket.balls,
    mainGridTotal: ticket.main_grid_total,
    editedNumbers: state.balls,
  });

  const stakeConstraints = useMemo(() => ({ minStake, maxStake }), [minStake, maxStake]);

  const ballConstraints = useMemo(
    () => ({
      minNormalCount: ballCountsData.minNormalCount,
      maxNormalCount: ballCountsData.maxNormalCount,
      requiredBonusCount: ballCountsData.requiredBonusCount,
      mainGridTotal: ticket.main_grid_total,
      secondaryGridTotal: ticket.secondary_grid_total,
      secondaryUnique: ticket.secondary_unique,
    }),
    [
      ballCountsData.minNormalCount,
      ballCountsData.maxNormalCount,
      ballCountsData.requiredBonusCount,
      ticket.main_grid_total,
      ticket.secondary_grid_total,
      ticket.secondary_unique,
    ]
  );

  const isStakeValid = useMemo(() => {
    const result = validateStake(state.stake, stakeConstraints);
    return result.isValid && stakeInputHook.isValid;
  }, [state.stake, stakeConstraints, stakeInputHook.isValid]);

  const isTicketValid = useMemo(() => {
    if (!hasBalls) return isStakeValid;
    const hasNaNs = state.balls.some((n) => Number.isNaN(n.value));
    return !hasNaNs && !state.validationError && isStakeValid;
  }, [hasBalls, state.balls, state.validationError, isStakeValid]);

  const validateBeforeSubmit = useCallback(() => {
    if (!hasBalls) return isStakeValid;
    const result = validateBalls(state.balls, ballConstraints);
    if (!result.isValid && result.errors.length > 0) {
      dispatch({ type: 'SET_VALIDATION_ERROR', payload: result.errors[0] });
      return false;
    }
    dispatch({ type: 'SET_VALIDATION_ERROR', payload: null });
    return isStakeValid;
  }, [hasBalls, state.balls, ballConstraints, isStakeValid]);

  const potentialPayout = useMemo(
    () =>
      calculatePotentialPayout(
        state.stake,
        ticket.outcome?.odds,
        ballCountsData.singleBetsCount,
        state.repeatForDraws
      ),
    [state.stake, ticket.outcome?.odds, ballCountsData.singleBetsCount, state.repeatForDraws]
  );

  const totalCost = useMemo(
    () => state.stake * ballCountsData.singleBetsCount * state.repeatForDraws,
    [state.stake, ballCountsData.singleBetsCount, state.repeatForDraws]
  );

  const notifyParentOfUpdate = useCallback(() => {
    if (!onTicketUpdateRef.current) return;
    const baseTicket = baseTicketRef.current;

    if (isTicketValid) {
      onTicketUpdateRef.current({
        ...baseTicket,
        stake: state.stake,
        balls: hasBalls ? state.balls : null,
        repeat_for_draws: state.repeatForDraws,
        change_odds_agreement: state.acceptOddsChanges,
        isModified: true,
        initialEditMode: false,
      });
    } else {
      onTicketUpdateRef.current(null);
    }
  }, [
    isTicketValid,
    hasBalls,
    state.balls,
    state.stake,
    state.repeatForDraws,
    state.acceptOddsChanges,
  ]);

  useEffect(() => {
    notifyParentOfUpdate();
  }, [notifyParentOfUpdate]);

  const actions = useMemo(
    () => ({
      setStake: (stake) => dispatch({ type: 'SET_STAKE', payload: stake }),
      setBalls: (balls) => {
        const oldBalls = stateRef.current.balls;
        dispatch({ type: 'SET_BALLS', payload: balls });
        emitFieldChange('balls', oldBalls, balls);
      },
      setRepeatForDraws: (draws) => {
        const oldDraws = stateRef.current.repeatForDraws;
        dispatch({ type: 'SET_REPEAT_FOR_DRAWS', payload: draws });
        emitFieldChange('repeat_for_draws', oldDraws, draws);
      },
      setAcceptOddsChanges: (accept) => {
        const oldAccept = stateRef.current.acceptOddsChanges;
        dispatch({ type: 'SET_ACCEPT_ODDS_CHANGES', payload: accept });
        emitFieldChange('change_odds_agreement', oldAccept, accept);
      },
      setValidationError: (error) =>
        dispatch({ type: 'SET_VALIDATION_ERROR', payload: error }),
      handleStakeChange: stakeInputHook.handleChange,
      toggleStakeEdit: stakeInputHook.toggleEditing,
      stopStakeEdit: stakeInputHook.stopEditing,
      clearErrors: () => dispatch({ type: 'CLEAR_ERRORS' }),
    }),
    [
      stakeInputHook.handleChange,
      stakeInputHook.toggleEditing,
      stakeInputHook.stopEditing,
      emitFieldChange,
    ]
  );

  const handleAddToCart = useCallback(
    (ticketRef) => {
      if (!validateBeforeSubmit() || !isStakeValid) return;

      try {
        dispatch({ type: 'SET_ADD_TO_CART_ERROR', payload: null });

        const cartItem = buildCartItemFromTicket(
          ticket,
          hasBalls ? state.balls : null,
          state.stake,
          potentialPayout,
          state.repeatForDraws,
          state.acceptOddsChanges,
          ballCountsData.singleBetsCount
        );

        reduxDispatch(addToCart(cartItem));

        trackLottoEvent?.('ticket_added_to_cart', {
          ticket_id: ticket.ticket_id,
          draw_id: ticket.draw_id,
          draw_number: ticket.draw_number,
          draw_date: ticket.draw_date,
          lottery_name: ticket.lottery_name,
          market_type: ticket.market_type,
          market_title: ticket.market_title,
          outcome: ticket.outcome,
          balls: hasBalls ? state.balls : null,
          stake: state.stake * LOTTO_CONSTANTS.CENTS_MULTIPLIER,
          potential_payout: potentialPayout,
          repeat_for_draws: state.repeatForDraws,
        });

        if (ticketRef?.current) {
          setFlyingTicketSource?.(ticketRef.current);
        }
      } catch (error) {
        dispatch({
          type: 'SET_ADD_TO_CART_ERROR',
          payload: 'Failed to add to cart. Please try again.',
        });
      }
    },
    [
      validateBeforeSubmit,
      isStakeValid,
      ticket,
      hasBalls,
      state.balls,
      state.stake,
      potentialPayout,
      state.repeatForDraws,
      state.acceptOddsChanges,
      ballCountsData.singleBetsCount,
      reduxDispatch,
      setFlyingTicketSource,
      trackLottoEvent,
    ]
  );

  return {
    state: {
      stake: state.stake,
      stakeInput: state.stakeInput,
      balls: state.balls,
      repeatForDraws: state.repeatForDraws,
      acceptOddsChanges: state.acceptOddsChanges,
      isEditingStake: state.isEditingStake,
    },
    validation: {
      validationError: state.validationError,
      addToCartError: state.addToCartError,
      isStakeValid,
      isTicketValid,
      validateBeforeSubmit,
    },
    actions,
    derived: {
      potentialPayout,
      totalCost,
      singleBetsCount: ballCountsData.singleBetsCount,
      minNormalCount: ballCountsData.minNormalCount,
      maxNormalCount: ballCountsData.maxNormalCount,
      requiredBonusCount: ballCountsData.requiredBonusCount,
    },
    handlers: { handleAddToCart },
  };
};
